import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDebounce } from 'use-debounce'
import { Building2, Loader2, Search, Sparkles, UserRound } from 'lucide-react'
import { useAssistant } from '@/components/assistant/AssistantProvider'
import { ApiInstance } from '@/lib/api'
import { assistantAskFirst, assistantShortcutLabel } from '@/lib/assistantShell'
import type { Paginated } from '@/lib/pagination'
import { usePermission } from '@/providers/permissions'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Button } from '@/components/ui/button'

interface CustomerHit {
  id: string
  name: string
  email?: string | null
  type: 'customer'
}

interface RealStateHit {
  id: string
  code?: string | null
  title: string
  type: 'real-state'
}

type SearchHit = CustomerHit | RealStateHit

interface SearchResults {
  customers: CustomerHit[]
  realStates: RealStateHit[]
  failed: boolean
}

const EMPTY_RESULTS: SearchResults = { customers: [], realStates: [], failed: false }

const PER_GROUP = 5

const ASK_VALUE = 'ask-assistant'

const hitValue = (hit: SearchHit) => (hit.type === 'customer' ? `c-${hit.id}` : `r-${hit.id}`)

function shortcutHint(): string {
  const mac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.userAgent)
  return mac ? '⌘ K' : 'Ctrl K'
}

async function searchGroup<T>(path: string, term: string, allowed: boolean): Promise<T[] | null> {
  if (!allowed) return []
  try {
    const { data } = await ApiInstance.get<Paginated<T>>(path, { params: { search: term, perPage: PER_GROUP } })
    return (data?.rows ?? []).slice(0, PER_GROUP)
  } catch {
    return null
  }
}

async function searchEverything(term: string, can: { customers: boolean; realStates: boolean }): Promise<SearchResults> {
  const [customers, realStates] = await Promise.all([
    searchGroup<Omit<CustomerHit, 'type'>>('/customers', term, can.customers),
    searchGroup<Omit<RealStateHit, 'type'>>('/real-state', term, can.realStates)
  ])
  return {
    customers: (customers ?? []).map(row => ({ ...row, type: 'customer' as const })),
    realStates: (realStates ?? []).map(row => ({ ...row, type: 'real-state' as const })),
    failed: (customers === null || realStates === null) && !customers?.length && !realStates?.length
  }
}

export function GlobalSearch() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [debouncedQuery] = useDebounce(query.trim(), 350)
  const [results, setResults] = useState<SearchResults>(EMPTY_RESULTS)
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const { hasPermission } = usePermission()
  const canCustomers = hasPermission('customers', 'read')
  const canRealStates = hasPermission('real-state', 'read')
  const { enabled: canAsk, ask } = useAssistant()
  const askedRef = useRef(false)
  const [selected, setSelected] = useState('')

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.key.toLowerCase() !== 'k' || event.defaultPrevented) return
      if (event.target instanceof HTMLElement && event.target.isContentEditable) return
      const otherDialog = document.querySelector(
        '[role="dialog"][data-state="open"]:not([data-global-search]):not([data-assistant]), [role="alertdialog"][data-state="open"]'
      )
      if (otherDialog) return
      event.preventDefault()
      setOpen(current => !current)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  useEffect(() => {
    if (!debouncedQuery) {
      setResults(EMPTY_RESULTS)
      return
    }
    let cancelled = false
    setLoading(true)
    searchEverything(debouncedQuery, { customers: canCustomers, realStates: canRealStates })
      .then(hits => {
        if (!cancelled) setResults(hits)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [debouncedQuery, canCustomers, canRealStates])

  const changeOpen = (next: boolean) => {
    setOpen(next)
    if (!next) {
      setQuery('')
      setResults(EMPTY_RESULTS)
      setSelected('')
    }
  }

  const select = useCallback(
    (hit: SearchHit) => {
      changeOpen(false)
      navigate(hit.type === 'customer' ? `/clientes/${hit.id}` : `/imoveis/${hit.id}`)
    },
    [navigate]
  )

  const askAssistant = () => {
    const question = query.trim()
    if (!question) return
    askedRef.current = true
    changeOpen(false)
    ask(question)
  }

  const { customers, realStates, failed } = results
  const hasResults = customers.length + realStates.length > 0
  const pending = query.trim() !== debouncedQuery || loading
  const searchLabel = canAsk ? 'Buscar ou perguntar ao assistente' : 'Buscar imóveis ou clientes'
  const askFirst = canAsk && assistantAskFirst(query, hasResults)
  const firstHit = realStates[0] ?? customers[0]
  const leadValue = askFirst ? ASK_VALUE : firstHit ? hitValue(firstHit) : canAsk && query.trim() ? ASK_VALUE : ''

  useEffect(() => {
    setSelected(leadValue)
  }, [leadValue])

  const askGroup = canAsk ? (
    <CommandGroup heading="Assistente">
      <CommandItem value={ASK_VALUE} onSelect={askAssistant} className="gap-3 py-2">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-primary/20 bg-accent text-accent-foreground">
          <Sparkles className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium">Perguntar ao assistente: “{query.trim()}”</span>
          <span className="block truncate text-[11px] text-muted-foreground">Respostas com os dados do seu painel</span>
        </span>
      </CommandItem>
    </CommandGroup>
  ) : null

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="hidden h-9 w-64 items-center gap-2 rounded-lg border border-input bg-field px-3 text-[13px] text-muted-foreground shadow-xs transition-colors hover:border-primary/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:flex"
      >
        <Search className="h-4 w-4 shrink-0" />
        <span className="flex-1 truncate text-left">{canAsk ? 'Buscar ou perguntar…' : 'Buscar imóveis ou clientes'}</span>
        <kbd className="rounded-md border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px]">{shortcutHint()}</kbd>
      </button>
      <Button variant="ghost" size="icon" className="lg:hidden" aria-label={searchLabel} onClick={() => setOpen(true)}>
        <Search className="h-[18px] w-[18px]" />
      </Button>

      <Dialog open={open} onOpenChange={changeOpen}>
        <DialogContent
          data-global-search
          hideCloseButton
          onCloseAutoFocus={event => {
            if (!askedRef.current) return
            askedRef.current = false
            event.preventDefault()
            document.querySelector<HTMLTextAreaElement>('[data-assistant] textarea')?.focus()
          }}
          className="top-[18%] max-w-xl translate-y-0 overflow-hidden p-0 data-[state=closed]:slide-out-to-top-[2%] data-[state=open]:slide-in-from-top-[2%]"
        >
          <DialogTitle className="sr-only">Busca global</DialogTitle>
          <DialogDescription className="sr-only">
            {canAsk
              ? 'Encontre imóveis e clientes pelo nome, código ou e-mail, ou faça uma pergunta ao assistente.'
              : 'Encontre imóveis e clientes pelo nome, código ou e-mail.'}
          </DialogDescription>
          <Command shouldFilter={false} value={selected} onValueChange={setSelected} className="rounded-none">
            <CommandInput
              value={query}
              onValueChange={setQuery}
              placeholder={canAsk ? 'Buscar ou perguntar ao assistente…' : 'Buscar por nome, código ou e-mail...'}
            />
            <CommandList className="max-h-[min(60vh,420px)]">
              {query.trim() === '' ? (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                  <p>Digite para buscar imóveis e clientes.</p>
                  {canAsk && (
                    <p className="mt-2 inline-flex items-center gap-1.5 text-xs">
                      <Sparkles className="h-3.5 w-3.5 text-primary" />
                      Dica: pergunte qualquer coisa ao assistente ({assistantShortcutLabel()})
                    </p>
                  )}
                </div>
              ) : (
                <>
                  {askFirst && askGroup}
                  {pending && !hasResults ? (
                    <p className="flex items-center justify-center gap-2 px-4 py-8 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" /> Buscando...
                    </p>
                  ) : (
                    !hasResults && (
                      <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                        {failed ? 'Não foi possível buscar agora. Tente novamente em instantes.' : `Nenhum resultado para “${query.trim()}”.`}
                      </p>
                    )
                  )}
                  {realStates.length > 0 && (
                    <CommandGroup heading="Imóveis">
                      {realStates.map(hit => (
                        <CommandItem key={hitValue(hit)} value={hitValue(hit)} onSelect={() => select(hit)} className="gap-3 py-2">
                          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-border bg-secondary text-muted-foreground">
                            <Building2 className="h-4 w-4" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-medium">{hit.title}</span>
                            {hit.code && <span className="block truncate font-mono text-[11px] text-muted-foreground">{hit.code}</span>}
                          </span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                  {customers.length > 0 && (
                    <CommandGroup heading="Clientes">
                      {customers.map(hit => (
                        <CommandItem key={hitValue(hit)} value={hitValue(hit)} onSelect={() => select(hit)} className="gap-3 py-2">
                          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-border bg-secondary text-muted-foreground">
                            <UserRound className="h-4 w-4" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-medium">{hit.name}</span>
                            {hit.email && <span className="block truncate text-[11px] text-muted-foreground">{hit.email}</span>}
                          </span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                  {!askFirst && askGroup}
                </>
              )}
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>
    </>
  )
}
